import { create } from "zustand";
import { apiClient } from "@/api/client";
import { AxiosError } from "axios";

export interface NotificationPreferenceItem {
  action: string;
  title: string;
  description: string;
  enabled: boolean;
  default_enabled: boolean;
}

// Dvippatel: Added consistent actor interface for notifications
export interface NotificationActor {
  id: string;
  name: string;
  avatar_url_signed?: string | null;
  avatar_updated_at?: string | null;
}


export interface NotificationPreferenceGroup {
  group_key: string;
  display: string;
  enabled: boolean;
  items: NotificationPreferenceItem[];
}

export interface NotificationPreferences {
  emailNotifications?: boolean;
  showUnreadCount?: boolean;
  groups?: NotificationPreferenceGroup[];
  [key: string]: any;
}

export interface NotificationDetail {
  id: string;
  group_key: string;
  action: string;
  title: string;
  message: string;
  entity_type: string;
  entity_id: string;
  actor_id: string;
  metadata: {
    new_values?: any;
    previous_values?: any;
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Dvippatel: Modified Notification interface to include consistent actor
// export interface Notification {
//   id: string;
//   notification_id: string;
//   user_id: string;
//   is_read: boolean;
//   read_at: string | null;
//   is_deleted: boolean;
//   delivery_status: string;
//   created_at: string;
//   updated_at: string;
//   deleted_at: string | null;
//   notification: NotificationDetail;
// }

// Proposed Change
export interface Notification {
  id: string;
  notification_id: string;
  user_id: string;

  is_read: boolean;
  read_at: string | null;
  is_deleted: boolean;
  delivery_status: string;

  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  // 👇 flattened fields (IMPORTANT)
  /** In-app path the notification points at, e.g. "/campaign/<id>". */
  action_url?: string | null;
  cta_label?: string | null;
  group_key: string;
  action: string;
  display_title: string;
  display_message: string;
  entity_type: string;
  entity_id: string;
  metadata: Record<string, any>;

  actor?: NotificationActor | null;
}

// End Proposed Change

export interface NotificationResponse {
  data: Notification[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    /** Unread across all pages; independent of filters. */
    unread_count?: number;
  };
}

/**
 * Where opening a notification should take the user, or null when it points
 * nowhere in the app. Prefers the server's `action_url` (relative paths only),
 * then falls back to the campaign the notification is about.
 */
export const notificationLink = (notification: Notification): string | null => {
  const url = notification.action_url;
  if (typeof url === "string" && url.startsWith("/") && !url.startsWith("//")) {
    return url;
  }
  const campaignId = notification.metadata?.campaign_id;
  if (typeof campaignId === "string" && campaignId !== "") {
    return `/campaign/${campaignId}`;
  }
  return null;
};

interface NotificationState {
  preferences: NotificationPreferences | null;
  notifications: Notification[];
  /** The latest few, for the header bell. */
  recent: Notification[];
  isLoading: boolean;
  unreadCount: number;

  getPreferences: () => Promise<void>;
  setPreferences: (preferences: NotificationPreferences) => void;
  /** Toggle locally, then save; reverts and throws if the save fails. */
  toggleGroup: (groupKey: string) => Promise<void>;
  toggleItem: (groupKey: string, action: string) => Promise<void>;
  /** Turn several groups on or off together (one toggle in the UI); saves only them. */
  setGroupsEnabled: (groupKeys: string[], enabled: boolean) => Promise<void>;
  /** Save the given groups, or all of them; untouched defaults stay defaults. */
  savePreferences: (groupKeys?: string[]) => Promise<void>;
  getNotifications: () => Promise<void>;
  getRecentNotifications: (limit?: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
}

const useNotificationStore = create<NotificationState>((set, get) => ({
  preferences: null,
  notifications: [],
  recent: [],
  isLoading: false,
  unreadCount: 0,

  getPreferences: async () => {
    set({ isLoading: true });
    try {
      const response = await apiClient.get<any>("/notification/preferences");

      // Handle different possible response structures
      let preferencesData: NotificationPreferences;

      // Check if response.data is an array (the groups directly)
      if (Array.isArray(response.data)) {
        preferencesData = {
          groups: response.data,
        };
      }
      // Check if response.data.data exists
      else if (response.data?.data) {
        if (Array.isArray(response.data.data)) {
          preferencesData = {
            groups: response.data.data,
          };
        } else {
          preferencesData = response.data.data;
        }
      }
      // Use response.data directly
      else {
        preferencesData = response.data || {};
      }

      // Log for debugging
      console.log("Notification Preferences Response:", response.data);
      console.log("Parsed Preferences:", preferencesData);

      set({
        preferences: preferencesData,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to fetch notification preferences";
      throw new Error(errorMessage);
    }
  },

  setPreferences: (preferences: NotificationPreferences) => {
    set({ preferences });
    // Store in localStorage for persistence
    localStorage.setItem("notificationPreferences", JSON.stringify(preferences));
  },

  toggleGroup: async (groupKey: string) => {
    const currentPreferences = get().preferences;
    if (!currentPreferences?.groups) return;

    const updatedGroups = currentPreferences.groups.map((group) => {
      if (group.group_key === groupKey) {
        const newEnabled = !group.enabled;
        return {
          ...group,
          enabled: newEnabled,
          items: group.items.map((item) => ({
            ...item,
            enabled: newEnabled,
          })),
        };
      }
      return group;
    });

    const updatedPreferences = {
      ...currentPreferences,
      groups: updatedGroups,
    };

    get().setPreferences(updatedPreferences);

    try {
      await get().savePreferences([groupKey]);
    } catch (error) {
      // Show the user the state the server actually has.
      get().setPreferences(currentPreferences);
      throw error;
    }
  },

  toggleItem: async (groupKey: string, action: string) => {
    const currentPreferences = get().preferences;
    if (!currentPreferences?.groups) return;

    const updatedGroups = currentPreferences.groups.map((group) => {
      if (group.group_key === groupKey) {
        const updatedItems = group.items.map((item) => {
          if (item.action === action) {
            return { ...item, enabled: !item.enabled };
          }
          return item;
        });

        // Check if all items are enabled to update group enabled state
        const allEnabled = updatedItems.every((item) => item.enabled);
        const anyEnabled = updatedItems.some((item) => item.enabled);

        return {
          ...group,
          items: updatedItems,
          enabled: anyEnabled, // Group enabled if any item is enabled
        };
      }
      return group;
    });

    const updatedPreferences = {
      ...currentPreferences,
      groups: updatedGroups,
    };

    get().setPreferences(updatedPreferences);

    try {
      await get().savePreferences([groupKey]);
    } catch (error) {
      // Show the user the state the server actually has.
      get().setPreferences(currentPreferences);
      throw error;
    }
  },

  setGroupsEnabled: async (groupKeys: string[], enabled: boolean) => {
    const currentPreferences = get().preferences;
    if (!currentPreferences?.groups) return;

    get().setPreferences({
      ...currentPreferences,
      groups: currentPreferences.groups.map((group) =>
        groupKeys.includes(group.group_key)
          ? {
              ...group,
              enabled,
              items: group.items.map((item) => ({ ...item, enabled })),
            }
          : group
      ),
    });

    try {
      await get().savePreferences(groupKeys);
    } catch (error) {
      get().setPreferences(currentPreferences);
      throw error;
    }
  },

  savePreferences: async (groupKeys?: string[]) => {
    const groups = (get().preferences?.groups ?? []).filter(
      (group) => !groupKeys || groupKeys.includes(group.group_key)
    );
    try {
      await apiClient.put("/notification/preferences", {
        groups: groups.map((group) => ({
          group_key: group.group_key,
          enabled: group.enabled,
          muted_actions: (group.items ?? [])
            .filter((item) => !item.enabled)
            .map((item) => item.action),
        })),
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Failed to save notification preferences"
      );
    }
  },

  getRecentNotifications: async (limit = 7) => {
    try {
      const response = await apiClient.get<NotificationResponse>("/notification", {
        params: { per_page: limit },
      });
      const items = response.data?.data || [];
      set({
        recent: items,
        unreadCount:
          response.data?.meta?.unread_count ??
          items.filter((n) => !n.is_read).length,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      throw new Error(
        axiosError.response?.data?.message || "Failed to fetch notifications"
      );
    }
  },

  getNotifications: async () => {
    set({ isLoading: true });
    try {
      const response = await apiClient.get<NotificationResponse>("/notification");

      // Get notifications from response
      const notificationsData = response.data?.data || [];

      // Unread across every page when the server reports it.
      const unread =
        response.data?.meta?.unread_count ??
        notificationsData.filter((n) => !n.is_read).length;

      set({
        notifications: notificationsData,
        unreadCount: unread,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to fetch notifications";
      throw new Error(errorMessage);
    }
  },

  markAllAsRead: async () => {
    set({ isLoading: true });
    try {
      await apiClient.post("/notification/read-all");

      // Update local state
      const updatedNotifications = get().notifications.map((notification) => ({
        ...notification,
        is_read: true,
        read_at: new Date().toISOString(),
      }));

      set({
        notifications: updatedNotifications,
        recent: get().recent.map((n) => ({ ...n, is_read: true })),
        unreadCount: 0,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Failed to mark all as read";
      throw new Error(errorMessage);
    }
  },

  markAsRead: async (notificationId: string) => {
    const now = new Date().toISOString();
    const markRead = (list: Notification[]) =>
      list.map((n) => (n.id === notificationId ? { ...n, is_read: true, read_at: now } : n));
    const wasUnread =
      [...get().notifications, ...get().recent].find((n) => n.id === notificationId)
        ?.is_read === false;

    // Optimistic: opening a notification should never wait on this.
    set({
      notifications: markRead(get().notifications),
      recent: markRead(get().recent),
      unreadCount: Math.max(0, get().unreadCount - (wasUnread ? 1 : 0)),
    });

    try {
      await apiClient.post(`/notification/${notificationId}/read`);
    } catch {
      // Not fatal: it is simply still unread next time the list loads.
    }
  },
}));

export default useNotificationStore;
